// PhotographyFooter.js - Photography-themed footer section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a photography-themed footer section with links, contact info, and social media
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The footer section elements
 */
export const createPhotographyFooterElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Class names
  const footerClass = 'photography-footer';
  const containerClass = 'photography-footer-container';
  const topSectionClass = 'photography-footer-top';
  const brandSectionClass = 'photography-footer-brand';
  const logoClass = 'photography-footer-logo';
  const brandTextClass = 'photography-footer-brand-text';
  const brandDescClass = 'photography-footer-brand-desc';
  
  // Links sections
  const linksSectionClass = 'photography-footer-links-section';
  const linksColumnClass = 'photography-footer-links-column';
  const linksTitleClass = 'photography-footer-links-title';
  const linksListClass = 'photography-footer-links-list';
  const linkItemClass = 'photography-footer-link-item';
  const linkClass = 'photography-footer-link';
  
  // Contact section
  const contactSectionClass = 'photography-footer-contact';
  const contactTitleClass = 'photography-footer-contact-title';
  const contactInfoClass = 'photography-footer-contact-info';
  const contactItemClass = 'photography-footer-contact-item';
  
  // Social section
  const socialSectionClass = 'photography-footer-social';
  const socialTitleClass = 'photography-footer-social-title';
  const socialLinksClass = 'photography-footer-social-links';
  const socialLinkClass = 'photography-footer-social-link';
  
  // Bottom section
  const bottomSectionClass = 'photography-footer-bottom';
  const copyrightClass = 'photography-footer-copyright';
  const bottomLinksClass = 'photography-footer-bottom-links';
  const bottomLinkClass = 'photography-footer-bottom-link';

  // Desktop Styles
  const footerStyles = {
    position: 'relative',
    backgroundColor: '#0a0a0a',
    backgroundImage: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
    color: '#ffffff',
    paddingTop: '80px',
    paddingBottom: '40px',
    paddingLeft: '20px',
    paddingRight: '20px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden'
  };

  const containerStyles = {
    maxWidth: '1400px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px'
  };

  const topSectionStyles = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    gap: '60px',
    paddingBottom: '60px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    width: '100%'
  };

  const brandSectionStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  };

  const logoStyles = {
    fontSize: '32px',
    fontWeight: '300',
    color: '#d4af37',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    textDecoration: 'none',
    marginBottom: '16px'
  };

  const brandTextStyles = {
    fontSize: '18px',
    fontWeight: '500',
    color: '#ffffff',
    marginBottom: '12px'
  };

  const brandDescStyles = {
    fontSize: '16px',
    color: '#b0b0b0',
    lineHeight: '1.6',
    maxWidth: '300px'
  };

  const linksColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  };

  const linksTitleStyles = {
    fontSize: '18px',
    fontWeight: '500',
    color: '#d4af37',
    marginBottom: '20px',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  };

  const linksListStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    listStyle: 'none',
    padding: '0',
    margin: '0'
  };

  const linkItemStyles = {
    margin: '0'
  };

  const linkStyles = {
    color: '#b0b0b0',
    textDecoration: 'none',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    display: 'block',
    padding: '4px 0',
    lineHeight: '1.4'
  };

  const contactSectionStyles = {
    display: 'flex',
    flexDirection: 'column'
  };

  const contactTitleStyles = {
    fontSize: '18px',
    fontWeight: '500',
    color: '#d4af37',
    marginBottom: '20px',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  };

  const contactInfoStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  };

  const contactItemStyles = {
    color: '#b0b0b0',
    fontSize: '16px',
    lineHeight: '1.5'
  };

  const socialSectionStyles = {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '30px'
  };

  const socialTitleStyles = {
    fontSize: '18px',
    fontWeight: '500',
    color: '#d4af37',
    marginBottom: '20px',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  };

  const socialLinksStyles = {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px'
  };

  const socialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#d4af37',
    textDecoration: 'none',
    fontSize: '18px',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  };

  const bottomSectionStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '40px',
    marginTop: '40px',
    flexWrap: 'wrap',
    gap: '16px'
  };

  const copyrightStyles = {
    color: '#888888',
    fontSize: '14px'
  };

  const bottomLinksStyles = {
    display: 'flex',
    gap: '30px'
  };

  const bottomLinkStyles = {
    color: '#888888',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'all 0.3s ease'
  };

  // Tablet Styles
  const tabletTopSectionStyles = {
    gridTemplateColumns: '1fr 1fr',
    gap: '40px'
  };

  const tabletBottomSectionStyles = {
    flexDirection: 'column',
    gap: '20px',
    textAlign: 'center'
  };

  const tabletBottomLinksStyles = {
    justifyContent: 'center'
  };

  // Mobile Styles
  const mobileFooterStyles = {
    paddingTop: '60px',
    paddingBottom: '30px'
  };

  const mobileTopSectionStyles = {
    gridTemplateColumns: '1fr',
    gap: '40px',
    textAlign: 'center'
  };

  const mobileBrandDescStyles = {
    maxWidth: '100%'
  };

  const mobileBottomLinksStyles = {
    flexDirection: 'column',
    gap: '15px'
  };

  // Register styles
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(footerClass, footerStyles);
    newRegistry[DEVICES.DESKTOP].set(containerClass, containerStyles);
    newRegistry[DEVICES.DESKTOP].set(topSectionClass, topSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(brandSectionClass, brandSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(logoClass, logoStyles);
    newRegistry[DEVICES.DESKTOP].set(brandTextClass, brandTextStyles);
    newRegistry[DEVICES.DESKTOP].set(brandDescClass, brandDescStyles);
    newRegistry[DEVICES.DESKTOP].set(linksColumnClass, linksColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(linksTitleClass, linksTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(linksListClass, linksListStyles);
    newRegistry[DEVICES.DESKTOP].set(linkItemClass, linkItemStyles);
    newRegistry[DEVICES.DESKTOP].set(linkClass, linkStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSectionClass, contactSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(contactTitleClass, contactTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoClass, contactInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(contactItemClass, contactItemStyles);
    newRegistry[DEVICES.DESKTOP].set(socialSectionClass, socialSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(socialTitleClass, socialTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(socialLinksClass, socialLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(socialLinkClass, socialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(bottomSectionClass, bottomSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(copyrightClass, copyrightStyles);
    newRegistry[DEVICES.DESKTOP].set(bottomLinksClass, bottomLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(bottomLinkClass, bottomLinkStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(topSectionClass, tabletTopSectionStyles);
    newRegistry[DEVICES.TABLET].set(bottomSectionClass, tabletBottomSectionStyles);
    newRegistry[DEVICES.TABLET].set(bottomLinksClass, tabletBottomLinksStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(footerClass, mobileFooterStyles);
    newRegistry[DEVICES.MOBILE].set(topSectionClass, mobileTopSectionStyles);
    newRegistry[DEVICES.MOBILE].set(brandDescClass, mobileBrandDescStyles);
    newRegistry[DEVICES.MOBILE].set(bottomLinksClass, mobileBottomLinksStyles);
    
    return newRegistry;
  });

  // Add classes to global registry
  setGlobalClassNames(prev => new Set([
    ...prev,
    footerClass, containerClass, topSectionClass, brandSectionClass, logoClass,
    brandTextClass, brandDescClass, linksColumnClass, linksTitleClass, 
    linksListClass, linkItemClass, linkClass, contactSectionClass,
    contactTitleClass, contactInfoClass, contactItemClass, socialSectionClass,
    socialTitleClass, socialLinksClass, socialLinkClass, bottomSectionClass,
    copyrightClass, bottomLinksClass, bottomLinkClass
  ]));

  // Generate unique IDs
  const footerId = Date.now();
  const containerId = footerId + 1;
  const topSectionId = footerId + 2;
  
  // Brand section IDs
  const brandSectionId = footerId + 3;
  const logoId = footerId + 4;
  const brandTextId = footerId + 5;
  const brandDescId = footerId + 6;
  const socialSectionId = footerId + 7;
  const socialTitleId = footerId + 8;
  const socialLinksId = footerId + 9;
  const instagramLinkId = footerId + 10;
  const facebookLinkId = footerId + 11;
  const twitterLinkId = footerId + 12;
  const linkedinLinkId = footerId + 13;
  
  // Services links section IDs
  const servicesColumnId = footerId + 14;
  const servicesTitleId = footerId + 15;
  const servicesListId = footerId + 16;
  const weddingLinkId = footerId + 17;
  const portraitLinkId = footerId + 18;
  const eventLinkId = footerId + 19;
  const commercialLinkId = footerId + 20;
  
  // Company links section IDs
  const companyColumnId = footerId + 21;
  const companyTitleId = footerId + 22;
  const companyListId = footerId + 23;
  const aboutLinkId = footerId + 24;
  const portfolioLinkId = footerId + 25;
  const blogLinkId = footerId + 26;
  const careersLinkId = footerId + 27;
  
  // Contact section IDs
  const contactSectionId = footerId + 28;
  const contactTitleId = footerId + 29;
  const contactInfoId = footerId + 30;
  const addressItemId = footerId + 31;
  const phoneItemId = footerId + 32;
  const emailItemId = footerId + 33;
  
  // Bottom section IDs
  const bottomSectionId = footerId + 34;
  const copyrightId = footerId + 35;
  const bottomLinksId = footerId + 36;
  const privacyLinkId = footerId + 37;
  const termsLinkId = footerId + 38;
  const cookiesLinkId = footerId + 39;

  const elements = [
    // Main footer
    {
      id: footerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Photography Footer',
      parentId: null,
      className: footerClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'photography-footer',
        'data-component': 'footer',
        'data-section-type': 'footer',
        'data-page-id': currentPageId
      }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Container',
      parentId: footerId,
      hierarchyName: getHierarchyName('div'),
      className: containerClass,
      styles: {},
      children: [topSectionId, bottomSectionId],
      text: '',
      attributes: {
        'data-element': 'footer-container'
      }
    },

    // Top Section
    {
      id: topSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Top Section',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: topSectionClass,
      styles: {},
      children: [brandSectionId, servicesColumnId, companyColumnId, contactSectionId],
      text: '',
      attributes: {
        'data-element': 'footer-top-section'
      }
    },

    // Brand Section
    {
      id: brandSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Brand Section',
      parentId: topSectionId,
      hierarchyName: getHierarchyName('div'),
      className: brandSectionClass,
      styles: {},
      children: [logoId, brandTextId, brandDescId, socialSectionId],
      text: '',
      attributes: {
        'data-element': 'footer-brand-section'
      }
    },

    // Logo
    {
      id: logoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Logo',
      parentId: brandSectionId,
      hierarchyName: getHierarchyName('a'),
      className: logoClass,
      styles: {},
      children: [],
      text: 'Bilal Films',
      linkTo: 'index',
      attributes: {
        'data-element': 'footer-logo',
      }
    },

    // Brand Text
    {
      id: brandTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Footer Brand Text',
      parentId: brandSectionId,
      hierarchyName: getHierarchyName('h4'),
      className: brandTextClass,
      styles: {},
      children: [],
      text: 'Professional Photography Studio',
      attributes: {
        'data-element': 'footer-brand-text'
      }
    },

    // Brand Description
    {
      id: brandDescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Brand Description',
      parentId: brandSectionId,
      hierarchyName: getHierarchyName('p'),
      className: brandDescClass,
      styles: {},
      children: [],
      text: 'Capturing life\'s most precious moments with artistic vision and professional excellence. Creating timeless memories that last forever.',
      attributes: {
        'data-element': 'footer-brand-desc'
      }
    },

    // Social Section
    {
      id: socialSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Social Section',
      parentId: brandSectionId,
      hierarchyName: getHierarchyName('div'),
      className: socialSectionClass,
      styles: {},
      children: [socialTitleId, socialLinksId],
      text: '',
      attributes: {
        'data-element': 'footer-social-section'
      }
    },

    // Social Title
    {
      id: socialTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Social Title',
      parentId: socialSectionId,
      hierarchyName: getHierarchyName('h4'),
      className: socialTitleClass,
      styles: {},
      children: [],
      text: 'Follow Us',
      attributes: {
        'data-element': 'footer-social-title'
      }
    },

    // Social Links
    {
      id: socialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Links',
      parentId: socialSectionId,
      hierarchyName: getHierarchyName('div'),
      className: socialLinksClass,
      styles: {},
      children: [instagramLinkId, facebookLinkId, twitterLinkId, linkedinLinkId],
      text: '',
      attributes: {
        'data-element': 'footer-social-links'
      }
    },

    // Instagram Link
    {
      id: instagramLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Instagram Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [],
      text: '📷',
      attributes: {
        'data-element': 'footer-social-link',
        'href': 'https://instagram.com',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Instagram'
      }
    },

    // Facebook Link
    {
      id: facebookLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Facebook Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [],
      text: '📘',
      attributes: {
        'data-element': 'footer-social-link',
        'href': 'https://facebook.com',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Facebook'
      }
    },

    // Twitter Link
    {
      id: twitterLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [],
      text: '🐦',
      attributes: {
        'data-element': 'footer-social-link',
        'href': 'https://twitter.com',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Twitter'
      }
    },

    // LinkedIn Link
    {
      id: linkedinLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'LinkedIn Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [],
      text: '💼',
      attributes: {
        'data-element': 'footer-social-link',
        'href': 'https://linkedin.com',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'LinkedIn'
      }
    },

    // Services Column
    {
      id: servicesColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Services Column',
      parentId: topSectionId,
      hierarchyName: getHierarchyName('div'),
      className: linksColumnClass,
      styles: {},
      children: [servicesTitleId, servicesListId],
      text: '',
      attributes: {
        'data-element': 'footer-links-column'
      }
    },

    // Services Title
    {
      id: servicesTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Services Title',
      parentId: servicesColumnId,
      hierarchyName: getHierarchyName('h4'),
      className: linksTitleClass,
      styles: {},
      children: [],
      text: 'Services',
      attributes: {
        'data-element': 'footer-links-title'
      }
    },

    // Services List
    {
      id: servicesListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Services List',
      parentId: servicesColumnId,
      hierarchyName: getHierarchyName('div'),
      className: linksListClass,
      styles: {},
      children: [weddingLinkId, portraitLinkId, eventLinkId, commercialLinkId],
      text: '',
      attributes: {
        'data-element': 'footer-links-list'
      }
    },

    // Wedding Photography Link
    {
      id: weddingLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Wedding Link',
      parentId: servicesListId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Wedding Photography',
      linkTo: 'wedding-photography',
      attributes: {
        'data-element': 'footer-link'
      }
    },

    // Portrait Photography Link
    {
      id: portraitLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Portrait Link',
      parentId: servicesListId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Portrait Sessions',
      linkTo: 'portrait-sessions',
      attributes: {
        'data-element': 'footer-link'
      }
    },

    // Event Photography Link
    {
      id: eventLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Event Link',
      parentId: servicesListId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Event Photography',
      linkTo: 'event-photography',
      attributes: {
        'data-element': 'footer-link'
      }
    },

    // Commercial Photography Link
    {
      id: commercialLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Commercial Link',
      parentId: servicesListId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Commercial Work',
      linkTo: 'commercial-work',
      attributes: {
        'data-element': 'footer-link'
      }
    },

    // Company Column
    {
      id: companyColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Company Column',
      parentId: topSectionId,
      hierarchyName: getHierarchyName('div'),
      className: linksColumnClass,
      styles: {},
      children: [companyTitleId, companyListId],
      text: '',
      attributes: {
        'data-element': 'footer-links-column'
      }
    },

    // Company Title
    {
      id: companyTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Company Title',
      parentId: companyColumnId,
      hierarchyName: getHierarchyName('h4'),
      className: linksTitleClass,
      styles: {},
      children: [],
      text: 'Company',
      attributes: {
        'data-element': 'footer-links-title'
      }
    },

    // Company List
    {
      id: companyListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Company List',
      parentId: companyColumnId,
      hierarchyName: getHierarchyName('div'),
      className: linksListClass,
      styles: {},
      children: [aboutLinkId, portfolioLinkId, blogLinkId, careersLinkId],
      text: '',
      attributes: {
        'data-element': 'footer-links-list'
      }
    },

    // About Link
    {
      id: aboutLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'About Link',
      parentId: companyListId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'About Us',
      linkTo: 'about',
      attributes: {
        'data-element': 'footer-link'
      }
    },

    // Portfolio Link
    {
      id: portfolioLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Portfolio Link',
      parentId: companyListId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Portfolio',
      linkTo: 'portfolio',
      attributes: {
        'data-element': 'footer-link'
      }
    },

    // Contact Section
    {
      id: contactSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Contact Section',
      parentId: topSectionId,
      hierarchyName: getHierarchyName('div'),
      className: contactSectionClass,
      styles: {},
      children: [contactTitleId, contactInfoId],
      text: '',
      attributes: {
        'data-element': 'footer-contact-section'
      }
    },

    // Contact Title
    {
      id: contactTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Contact Title',
      parentId: contactSectionId,
      hierarchyName: getHierarchyName('h4'),
      className: contactTitleClass,
      styles: {},
      children: [],
      text: 'Contact',
      attributes: {
        'data-element': 'footer-contact-title'
      }
    },

    // Contact Info
    {
      id: contactInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Info',
      parentId: contactSectionId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoClass,
      styles: {},
      children: [addressItemId, phoneItemId, emailItemId],
      text: '',
      attributes: {
        'data-element': 'footer-contact-info'
      }
    },

    // Address Item
    {
      id: addressItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Address Item',
      parentId: contactInfoId,
      hierarchyName: getHierarchyName('p'),
      className: contactItemClass,
      styles: {},
      children: [],
      text: '123 Photography Street, New York, NY 10001',
      attributes: {
        'data-element': 'footer-contact-item'
      }
    },

    // Phone Item
    {
      id: phoneItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Phone Item',
      parentId: contactInfoId,
      hierarchyName: getHierarchyName('p'),
      className: contactItemClass,
      styles: {},
      children: [],
      text: '+1 (555) 123-4567',
      attributes: {
        'data-element': 'footer-contact-item'
      }
    },

    // Email Item
    {
      id: emailItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Email Item',
      parentId: contactInfoId,
      hierarchyName: getHierarchyName('p'),
      className: contactItemClass,
      styles: {},
      children: [],
      text: 'hello@lensandlight.com',
      attributes: {
        'data-element': 'footer-contact-item'
      }
    },

    // Bottom Section
    {
      id: bottomSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Bottom Section',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: bottomSectionClass,
      styles: {},
      children: [copyrightId, bottomLinksId],
      text: '',
      attributes: {
        'data-element': 'footer-bottom-section'
      }
    },

    // Copyright
    {
      id: copyrightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Copyright',
      parentId: bottomSectionId,
      hierarchyName: getHierarchyName('p'),
      className: copyrightClass,
      styles: {},
      children: [],
      text: '© 2025 Bilal Films Photography Studio. All rights reserved.',
      attributes: {
        'data-element': 'footer-copyright'
      }
    },

    // Bottom Links
    {
      id: bottomLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Bottom Links',
      parentId: bottomSectionId,
      hierarchyName: getHierarchyName('div'),
      className: bottomLinksClass,
      styles: {},
      children: [privacyLinkId, termsLinkId, cookiesLinkId],
      text: '',
      attributes: {
        'data-element': 'footer-bottom-links'
      }
    },

    // Privacy Policy Link
    {
      id: privacyLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Privacy Policy Link',
      parentId: bottomLinksId,
      hierarchyName: getHierarchyName('a'),
      className: bottomLinkClass,
      styles: {},
      children: [],
      text: 'Privacy Policy',
      attributes: {
        'data-element': 'footer-bottom-link',
        'href': '#'
      }
    },

    // Terms of Service Link
    {
      id: termsLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Terms of Service Link',
      parentId: bottomLinksId,
      hierarchyName: getHierarchyName('a'),
      className: bottomLinkClass,
      styles: {},
      children: [],
      text: 'Terms of Service',
      attributes: {
        'data-element': 'footer-bottom-link',
        'href': '#'
      }
    },

    // Cookie Policy Link
    {
      id: cookiesLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Cookie Policy Link',
      parentId: bottomLinksId,
      hierarchyName: getHierarchyName('a'),
      className: bottomLinkClass,
      styles: {},
      children: [],
      text: 'Cookie Policy',
      attributes: {
        'data-element': 'footer-bottom-link',
        'href': '#'
      }
    }
  ];

  return {
    footerSection: elements[0],
    elements: elements
  };
}; 